import { useMemo } from "react";
import L from "leaflet";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";

import type { ChainDetail, ChainLoadOut } from "@/api";

/**
 * Chain-detail map.
 *
 * Renders each leg's loaded polyline in solid blue and each handoff's
 * deadhead polyline as dashed orange. Markers sit at every pickup +
 * the final drop, with popups for the load id and scheduled time.
 *
 * The polylines come from the backend (`route_on_highways` via the
 * bundled NTAD/TIGER graph). When no graph is bundled the polylines
 * degrade to 2-point straight lines — they still render correctly, just
 * not following actual highways.
 */

interface ChainMapProps {
  chain: ChainDetail;
  /** Height of the map container (Tailwind utility OK, defaults to h-96). */
  className?: string;
}

const LOADED_STYLE = {
  color: "#2563eb",     // brand-600 — solid blue
  weight: 4,
  opacity: 0.9,
};

const DEADHEAD_STYLE = {
  color: "#f59e0b",     // amber-500 — dashed orange for empty miles
  weight: 3,
  opacity: 0.85,
  dashArray: "8 6",
};

export function ChainMap({ chain, className }: ChainMapProps): JSX.Element {
  // Aggregate ALL polyline points to fit the map bounds.
  const allPoints = useMemo<[number, number][]>(() => {
    const out: [number, number][] = [];
    for (const leg of chain.loads) {
      out.push(...leg.loaded_polyline);
      out.push(...leg.deadhead_polyline);
    }
    return out;
  }, [chain]);

  if (allPoints.length === 0) {
    return (
      <div className="card p-6 text-center text-slate-500">
        No route geometry available for this chain.
      </div>
    );
  }

  const center: [number, number] = [
    allPoints.reduce((s, p) => s + p[0], 0) / allPoints.length,
    allPoints.reduce((s, p) => s + p[1], 0) / allPoints.length,
  ];

  return (
    <div className={`card overflow-hidden ${className ?? "h-96"}`}>
      <MapContainer
        center={center}
        zoom={6}
        scrollWheelZoom
        className="h-full w-full"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={allPoints} />
        {chain.loads.map((leg, idx) => (
          <LegLayer key={`${leg.seq}-${leg.load_id}`} leg={leg} isFirst={idx === 0} />
        ))}
        <TailMarker chain={chain} />
      </MapContainer>
    </div>
  );
}

interface LegLayerProps {
  leg: ChainLoadOut;
  isFirst: boolean;
}

function LegLayer({ leg, isFirst }: LegLayerProps): JSX.Element {
  return (
    <>
      {leg.deadhead_polyline.length > 0 && (
        <Polyline positions={leg.deadhead_polyline} pathOptions={DEADHEAD_STYLE} />
      )}
      {leg.loaded_polyline.length > 0 && (
        <Polyline positions={leg.loaded_polyline} pathOptions={LOADED_STYLE} />
      )}
      {leg.loaded_polyline.length > 0 && (
        <Marker position={leg.loaded_polyline[0]}>
          <Popup>
            <div className="text-xs">
              <div className="font-semibold">
                {isFirst ? "Chain start " : "Pickup "}#{leg.load_id}
              </div>
              <div className="text-slate-600">
                {new Date(leg.pickup_at).toLocaleString()}
              </div>
            </div>
          </Popup>
        </Marker>
      )}
    </>
  );
}

function TailMarker({ chain }: { chain: ChainDetail }): JSX.Element | null {
  const last = chain.loads[chain.loads.length - 1];
  if (!last || last.loaded_polyline.length === 0) return null;
  const tail = last.loaded_polyline[last.loaded_polyline.length - 1];
  return (
    <Marker position={tail}>
      <Popup>
        <div className="text-xs">
          <div className="font-semibold">Final drop #{last.load_id}</div>
          <div className="text-slate-600">
            {new Date(last.drop_at).toLocaleString()}
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

/** Helper that adjusts the map view to fit all points on first render. */
function FitBounds({ points }: { points: [number, number][] }): null {
  const map = useMap();
  // We compute LatLngBounds explicitly so we don't depend on react-leaflet's
  // `bounds` prop (which is set-and-forget on the MapContainer).
  useMemo(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [24, 24] });
  }, [map, points]);
  return null;
}
